/**
 * Workshop-Module-API. Brocken 2a: nur Read-Endpoints + Archetypen-Katalog.
 * Schreibende Endpoints (Skills generieren, verteilen, Voting) folgen.
 */
import { api } from "@/api/client";

export type ModuleKey =
  | "archetypes"
  | "golden_circle"
  | "personas"
  | "brandwheel"
  | "sales_story";

export type ModuleStatus = "pending" | "in_progress" | "completed" | "failed";

export interface ModuleState {
  id: string;
  workshop_id: string;
  module_key: ModuleKey;
  step: number;
  status: ModuleStatus;
  result: Record<string, unknown> | null;
  prompt_id: string | null;
  llm_call_id: string | null;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ArchetypeSpec {
  name: string;
  topic: string;
  color: string;
}

export interface ArchetypeQuadrant {
  name: string;
  archetypes: ArchetypeSpec[];
}

export async function listModuleStates(workshopId: string): Promise<ModuleState[]> {
  const { data } = await api.get<ModuleState[]>(`/v1/workshops/${workshopId}/modules`);
  return data;
}

export async function listModuleSteps(
  workshopId: string,
  moduleKey: ModuleKey,
): Promise<ModuleState[]> {
  const { data } = await api.get<ModuleState[]>(
    `/v1/workshops/${workshopId}/modules/${moduleKey}`,
  );
  return data;
}

export async function getModulePrimaryState(
  workshopId: string,
  moduleKey: ModuleKey,
): Promise<ModuleState | null> {
  const { data } = await api.get<ModuleState | null>(
    `/v1/workshops/${workshopId}/modules/${moduleKey}/state`,
  );
  return data;
}

export async function getArchetypesCatalog(
  workshopId: string,
): Promise<ArchetypeQuadrant[]> {
  const { data } = await api.get<ArchetypeQuadrant[]>(
    `/v1/workshops/${workshopId}/modules/archetypes/catalog`,
  );
  return data;
}

export async function generateArchetypesSkills(
  workshopId: string,
): Promise<ModuleState> {
  const { data } = await api.post<ModuleState>(
    `/v1/workshops/${workshopId}/modules/archetypes/generate-skills`,
  );
  return data;
}

export async function updateArchetypesSkills(
  workshopId: string,
  skills: string[],
): Promise<ModuleState> {
  const { data } = await api.put<ModuleState>(
    `/v1/workshops/${workshopId}/modules/archetypes/skills`,
    { skills },
  );
  return data;
}

export async function assignArchetypesSkills(
  workshopId: string,
): Promise<ModuleState> {
  const { data } = await api.post<ModuleState>(
    `/v1/workshops/${workshopId}/modules/archetypes/assign-skills`,
  );
  return data;
}

export async function updateArchetypesAssignments(
  workshopId: string,
  assignments: Record<string, string>,
): Promise<ModuleState> {
  const { data } = await api.put<ModuleState>(
    `/v1/workshops/${workshopId}/modules/archetypes/assignments`,
    { assignments },
  );
  return data;
}

export const ARCHETYPES_VOTES_DOTS_COUNT = 20;

export async function updateArchetypesVotes(
  workshopId: string,
  dots: string[],
): Promise<ModuleState> {
  const { data } = await api.put<ModuleState>(
    `/v1/workshops/${workshopId}/modules/archetypes/votes`,
    { dots },
  );
  return data;
}

// ──────────────────────────────────────────────
// Modul 2 — Golden Circle
// ──────────────────────────────────────────────
export type GoldenCircleLayer = "why" | "how" | "what";

export interface GoldenCircleCandidate {
  text: string;
  theme: string;
}

export async function generateGoldenCircle(
  workshopId: string,
  layer: GoldenCircleLayer,
): Promise<ModuleState> {
  const { data } = await api.post<ModuleState>(
    `/v1/workshops/${workshopId}/modules/golden_circle/${layer}/generate`,
  );
  return data;
}

export async function selectGoldenCircle(
  workshopId: string,
  layer: GoldenCircleLayer,
  selectedIndex: number | null,
): Promise<ModuleState> {
  const { data } = await api.put<ModuleState>(
    `/v1/workshops/${workshopId}/modules/golden_circle/${layer}/select`,
    { selected_index: selectedIndex },
  );
  return data;
}

// ──────────────────────────────────────────────
// Modul 3 — Personas
// ──────────────────────────────────────────────
export interface TargetGroup {
  name: string;
  description: string;
}

export interface Persona {
  target_group_index: number;
  first_name: string;
  last_name: string;
  gender: string;
  age: number;
  residence: string;
  education: string;
  marital_status: string;
  income: string;
  description: string;
  goals: string;
  concerns: string;
  touchpoints: string[];
  image_url: string | null;
}

export async function generateTargetGroups(workshopId: string): Promise<ModuleState> {
  const { data } = await api.post<ModuleState>(
    `/v1/workshops/${workshopId}/modules/personas/target-groups/generate`,
  );
  return data;
}

export async function updateTargetGroups(
  workshopId: string,
  targetGroups: TargetGroup[],
): Promise<ModuleState> {
  const { data } = await api.put<ModuleState>(
    `/v1/workshops/${workshopId}/modules/personas/target-groups`,
    { target_groups: targetGroups },
  );
  return data;
}

export async function generatePersona(
  workshopId: string,
  targetGroupIndex: number,
): Promise<ModuleState> {
  const { data } = await api.post<ModuleState>(
    `/v1/workshops/${workshopId}/modules/personas/persona/${targetGroupIndex}/generate`,
  );
  return data;
}

export async function updatePersona(
  workshopId: string,
  targetGroupIndex: number,
  patch: Partial<Omit<Persona, "target_group_index" | "image_url">>,
): Promise<ModuleState> {
  const { data } = await api.put<ModuleState>(
    `/v1/workshops/${workshopId}/modules/personas/persona/${targetGroupIndex}`,
    patch,
  );
  return data;
}

export async function generatePersonaImage(
  workshopId: string,
  targetGroupIndex: number,
): Promise<ModuleState> {
  const { data } = await api.post<ModuleState>(
    `/v1/workshops/${workshopId}/modules/personas/persona/${targetGroupIndex}/generate-image`,
  );
  return data;
}

// ──────────────────────────────────────────────
// Modul 4 — Markensteuerrad (Brandwheel)
// ──────────────────────────────────────────────
export type BrandwheelSegment =
  | "brand_core"
  | "benefits"
  | "tone"
  | "attributes"
  | "image";

export const BRANDWHEEL_STEP: Record<BrandwheelSegment, number> = {
  brand_core: 1,
  benefits: 2,
  tone: 3,
  attributes: 4,
  image: 5,
};

export async function generateBrandwheel(
  workshopId: string,
  segment: BrandwheelSegment,
): Promise<ModuleState> {
  const { data } = await api.post<ModuleState>(
    `/v1/workshops/${workshopId}/modules/brandwheel/${segment}/generate`,
  );
  return data;
}

export async function updateBrandwheelSegment(
  workshopId: string,
  segment: BrandwheelSegment,
  items: string[],
): Promise<ModuleState> {
  const { data } = await api.put<ModuleState>(
    `/v1/workshops/${workshopId}/modules/brandwheel/${segment}`,
    { items },
  );
  return data;
}

// ──────────────────────────────────────────────
// Modul 5 — Sales Story
// ──────────────────────────────────────────────
export interface SalesStory {
  target_group_index: number;
  hook: string;
  pitch: string;
  cta: string;
  short_variant: string;
}

export async function generateSalesStory(
  workshopId: string,
  targetGroupIndex: number,
): Promise<ModuleState> {
  const { data } = await api.post<ModuleState>(
    `/v1/workshops/${workshopId}/modules/sales_story/persona/${targetGroupIndex}/generate`,
  );
  return data;
}

export async function updateSalesStory(
  workshopId: string,
  targetGroupIndex: number,
  patch: Partial<Omit<SalesStory, "target_group_index">>,
): Promise<ModuleState> {
  const { data } = await api.put<ModuleState>(
    `/v1/workshops/${workshopId}/modules/sales_story/persona/${targetGroupIndex}`,
    patch,
  );
  return data;
}
